import { Context } from "csi/def/Context";
import declare from "dojo/_base/declare";
import StringConstructor from "csi/heading/String";
import csiHeading from "csi/heading/_base";
import xml from "csi/xml";
import InlineSelect from "csi/widget/InlineSelect";
import Spec, { QuerySpec } from "csi/core/Spec";
import Heading from "csi/def/Heading";
import WidgetBase from "@dojo/framework/core/WidgetBase";

const StringTranslated = declare(StringConstructor, {
	// limit query operations to EQ/NE so the client can send full class names to the server to filter by (substrings won't work)
	queryOps: csiHeading.QUERY_OPS[xml.ATTR_ENUM],

	/**
	 * @override
	 * Show localized name and class name in filter drop-down
	 * NOTE: not completely sure of the difference between formatQueryFilterValue and formatValueForFiltering, but both needed to work fully
	 */
	formatQueryFilterValue(ctx: Context, value: string, format?: string, bFloatingDates?: boolean, bSelected?: boolean): string {
		return this._getTranslatedTextForFilter(ctx, value);
	},

	/**
	 * @override
	 * Show localized name and class name in filter drop-down
	 */
	formatValueForFiltering(ctx: Context, h: Heading, value: string): string {
		return this._getTranslatedTextForFilter(ctx, value);
	},

	/**
	 * @override
	 * Sort by localized name rather than class
	 */
	formatValueForSorting(value: string, format: string, formattedValue: string): string {
		return formattedValue;
	},

	/**
	 * @override
	 * Restrict blank query operators since this will never be blank, and restrict sorting since we do not load all results at once
	 */
	getQuerySpec(ctx: Context, h: Heading, spec: Spec): QuerySpec {
		const qs = this.inherited<QuerySpec>(arguments);
		qs.notSortable = true;
		qs.omitBlanks = true;
		return qs;
	},

	/**
	 * @override
	 * Use InlineSelect so the value stored is different than the text displayed (default is ComboBox which just saves the text displayed rather than the value)
	 */
	toHtmlQueryFilter(ctx: Context, spec: Spec, refNode: HTMLElement, params?: Record<string, any>): WidgetBase {
		const { store, value } = params;

		// ensure selected value is in store so it shows up before user clicks drop-down and store is loaded
		if (!store.isLoaded() && !store.getFromMemory(value)) {
			store.addSelectedValue(value, this._getTranslatedTextForFilter(ctx, value));
		}

		return new InlineSelect({
			labelProperty: store.labelProperty,
			...params,
		});
	},

	/**
	 * @override
	 * Show localized name in view
	 */
	toValue(ctx: Context, h: Heading): string {
		return this._getTranslatedText(ctx, this._getValue(ctx, h));
	},

	/**
	 * Override to return text do display in view; the Context has the node holding the value
	 */
	_getTranslatedText(ctx: Context, value: string): string {
		return value;
	},

	/**
	 * Override to return text to display when filtering; the Context here does not have the node holding the value, just the view
	 */
	_getTranslatedTextForFilter(ctx: Context, value: string): string {
		return value;
	},
});

export = StringTranslated;